/**
 * CreditDebitNoteService.ts
 *
 * Follows your exact pattern from InvoiceReceiptService.ts:
 *  - Uses axiosInstance (auth + refresh interceptor)
 *  - BASE_URL from utils/urls
 *  - Returns typed responses
 */

import axios from "../utils/axiosConfig";
import { BASE_URL } from "../utils/urls";
import {
  ICreateCreditNoteRequest,
  ICreateCreditNoteResponse,
  ICreateDebitNoteRequest,
  ICreateDebitNoteResponse,
  ICreditNote,
  IDebitNote,
  INoteListParams,
  INoteListResponse,
} from "../Interfaces/ICreditDebitNote";

const API = `${BASE_URL}/api`;

// ─── Credit Note ──────────────────────────────────────────────────────────────

/**
 * Create a new Credit Note.
 * Backend: POST /api/creditNote/create
 */
export const createCreditNote = async (
  data: ICreateCreditNoteRequest
): Promise<ICreateCreditNoteResponse> => {
  try {
    const response = await axios.post(`${API}/creditNote/create`, data, {
      headers: { "Content-Type": "application/json" },
    });
    return response.data;
  } catch (error: any) {
    console.error("Error creating credit note:", error?.response?.data || error?.message);
    throw new Error(error?.response?.data?.message || "Failed to create credit note");
  }
};

/**
 * Fetch credit notes for an org (with optional filters + pagination).
 * Backend: GET /api/creditNote/list
 */
export const getCreditNotes = async (
  params: INoteListParams
): Promise<INoteListResponse<ICreditNote>> => {
  try {
    const response = await axios.get(`${API}/creditNote/list`, { params });
    return response.data;
  } catch (error: any) {
    console.error("Error fetching credit notes:", error?.response?.data || error?.message);
    throw new Error(error?.response?.data?.message || "Failed to fetch credit notes");
  }
};

/**
 * Get a single credit note by ID.
 * Backend: GET /api/creditNote/:cnId
 */
export const getCreditNoteById = async (
  orgId: string,
  cnId: string
): Promise<ICreditNote> => {
  try {
    const response = await axios.get(`${API}/creditNote/${cnId}`, { params: { orgId } });
    return response.data.creditNote;
  } catch (error: any) {
    throw new Error(error?.response?.data?.message || "Failed to fetch credit note");
  }
};

/**
 * Cancel a credit note.
 * Backend: PUT /api/creditNote/:cnId/cancel
 */
export const cancelCreditNote = async (
  orgId: string,
  cnId: string,
  cancelledBy: string
): Promise<{ message: string }> => {
  try {
    const response = await axios.put(`${API}/creditNote/${cnId}/cancel`, {
      orgId,
      cancelledBy,
    });
    return response.data;
  } catch (error: any) {
    throw new Error(error?.response?.data?.message || "Failed to cancel credit note");
  }
};

// ─── Debit Note ───────────────────────────────────────────────────────────────

/**
 * Create a new Debit Note.
 * Backend: POST /api/debitNote/create
 */
export const createDebitNote = async (
  data: ICreateDebitNoteRequest
): Promise<ICreateDebitNoteResponse> => {
  try {
    const response = await axios.post(`${API}/debitNote/create`, data, {
      headers: { "Content-Type": "application/json" },
    });
    return response.data;
  } catch (error: any) {
    console.error("Error creating debit note:", error?.response?.data || error?.message);
    throw new Error(error?.response?.data?.message || "Failed to create debit note");
  }
};

/**
 * Fetch debit notes for an org (with optional filters + pagination).
 * Backend: GET /api/debitNote/list
 */
export const getDebitNotes = async (
  params: INoteListParams
): Promise<INoteListResponse<IDebitNote>> => {
  try {
    const response = await axios.get(`${API}/debitNote/list`, { params });
    return response.data;
  } catch (error: any) {
    throw new Error(error?.response?.data?.message || "Failed to fetch debit notes");
  }
};

/**
 * Get a single debit note by ID.
 * Backend: GET /api/debitNote/:dnId
 */
export const getDebitNoteById = async (
  orgId: string,
  dnId: string
): Promise<IDebitNote> => {
  try {
    const response = await axios.get(`${API}/debitNote/${dnId}`, { params: { orgId } });
    return response.data.debitNote;
  } catch (error: any) {
    throw new Error(error?.response?.data?.message || "Failed to fetch debit note");
  }
};

/**
 * Cancel a debit note.
 * Backend: PUT /api/debitNote/:dnId/cancel
 */
export const cancelDebitNote = async (
  orgId: string,
  dnId: string,
  cancelledBy: string
): Promise<{ message: string }> => {
  try {
    const response = await axios.put(`${API}/debitNote/${dnId}/cancel`, {
      orgId,
      cancelledBy,
    });
    return response.data;
  } catch (error: any) {
    throw new Error(error?.response?.data?.message || "Failed to cancel debit note");
  }
};